import { z } from 'zod'
import type { Organization } from '@/features/organizations/types/organization'
import type { ApiError } from '@/lib/api/errors'

type Translate = (key: string, named?: Record<string, unknown>) => string

/** Limits mirror the backend's Create/UpdateOrganizationRequest. */
export function organizationFormSchema(t: Translate) {
  const tooLong = (max: number) => t('organizations.validation.tooLong', { max })
  const optional = (max: number) => z.string().trim().max(max, tooLong(max))

  return z.object({
    name: z
      .string()
      .trim()
      .min(1, t('organizations.validation.nameRequired'))
      .max(255, tooLong(255)),
    companyName: z
      .string()
      .trim()
      .min(1, t('organizations.validation.companyNameRequired'))
      .max(255, tooLong(255)),
    email: z
      .string()
      .trim()
      .max(255, tooLong(255))
      .email(t('organizations.validation.emailInvalid'))
      .or(z.literal('')),
    vatNumber: optional(50),
    streetAddress: optional(255),
    zipCode: optional(20),
    city: optional(255),
    country: optional(255),
  })
}

export type OrganizationFormValues = z.infer<ReturnType<typeof organizationFormSchema>>

export const EMPTY_ORGANIZATION_FORM: OrganizationFormValues = {
  name: '',
  companyName: '',
  email: '',
  vatNumber: '',
  streetAddress: '',
  zipCode: '',
  city: '',
  country: '',
}

/** Server field names (snake_case) mapped to the form's field names. */
const SERVER_FIELDS: Record<string, keyof OrganizationFormValues> = {
  name: 'name',
  company_name: 'companyName',
  email: 'email',
  vat_number: 'vatNumber',
  street_address: 'streetAddress',
  zip_code: 'zipCode',
  city: 'city',
  country: 'country',
}

export function toOrganizationFormValues(organization: Organization): OrganizationFormValues {
  return {
    name: organization.name,
    companyName: organization.company_name ?? '',
    email: organization.email ?? '',
    vatNumber: organization.vat_number ?? '',
    streetAddress: organization.street_address ?? '',
    zipCode: organization.zip_code ?? '',
    city: organization.city ?? '',
    country: organization.country ?? '',
  }
}

/** A 422's field errors keyed by form field, keeping the first message of each. */
export function toOrganizationFieldErrors(
  error: unknown,
): Partial<Record<keyof OrganizationFormValues, string>> {
  const fieldErrors = (error as ApiError).fieldErrors ?? {}

  return Object.fromEntries(
    Object.entries(fieldErrors)
      .filter(([field]) => field in SERVER_FIELDS)
      .map(([field, messages]) => [SERVER_FIELDS[field], messages[0]]),
  )
}
